import * as React from 'react'
import { ReactInstance } from 'react'
import ReactTable from 'react-table'
import 'react-table/react-table.css'
import { Button, ButtonGroup, Form, Row, Col, Modal, Container } from 'react-bootstrap'
import ReactDOM from 'react-dom'
import { DownTime, Alarm, TimeOnOff } from '../../model/DownTime'
import { getAlarmsForDowntime } from '../../rest-api/Downtime-api'
import { DurationFormatter } from '../../common/Formatters'
import { getTechnologyString } from '../../model/SiteConfig'
import { DateTimePicker } from '../../common/DateTimePicker'

interface DispatchProps {
  split: (downtime: DownTime, splitTime: Date) => void
  close: () => void
  dateFormatter: (date?: Date, startOfPeriod?: Date) => JSX.Element
}

interface StateProps {
  downtime: DownTime
  siteTechnology: string
  marketKey: string
  fromDate: Date
}

type State = {
  alarms?: Alarm[]
  loadingAlarms: boolean
  splitTime: Date | undefined
  validated: boolean
}

type Props = DispatchProps & StateProps
const durationInMiliSeconds = (event: TimeOnOff): number =>
  (event.timeOff && event.timeOff && event.timeOff.getTime() - event.timeOn.getTime()) || 0

export class InfoSplitModal extends React.Component<Props, State> {
  dataTableElement?: ReactInstance
  dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'UTC'
  }
  constructor(props: Props) {
    super(props)

    this.state = {
      loadingAlarms: true,
      splitTime: props.downtime.timeOn,
      validated: false
    }
  }

  componentDidMount(): void {
    this.loadAlarms()
  }

  componentDidUpdate(previousProps: Readonly<Props>, previousState: Readonly<State>): void {
    if (this.dataTableElement) {
      let theads = (ReactDOM.findDOMNode(this.dataTableElement) as Element).getElementsByClassName(
        'rt-thead'
      )
      let tbody = (ReactDOM.findDOMNode(this.dataTableElement) as Element).getElementsByClassName(
        'rt-tbody'
      )[0]

      if (tbody.scrollHeight > tbody.clientHeight) {
        Array.from(theads).map(t => t.classList.add('vertical-scrollbar-present'))
      } else {
        Array.from(theads).map(t => t.classList.remove('vertical-scrollbar-present'))
      }
    }
  }

  private loadAlarms(): void {
    const timeOff = this.props.downtime.timeOff || new Date()
    getAlarmsForDowntime(
      this.props.marketKey,
      this.props.downtime.scadaObjectKey,
      this.props.downtime.timeOn,
      timeOff
    )
      .then(result =>
        this.setState({
          alarms: result,
          loadingAlarms: false
        })
      )
      .catch(err => {
        console.info('Failed to get alarms for the downtime event')
      })
  }

  public render() {
    const { downtime, siteTechnology, fromDate } = this.props
    const { alarms, loadingAlarms, splitTime, validated } = this.state

    const handleSplit = (split: Date | undefined) => {
      this.setState({
        splitTime: split,
        validated: checkValidity(splitTime)
      })
    }

    const checkValidity = (splitTime: Date | undefined) => {
      if (!splitTime) {
        return false
      }

      return (
        !isNaN(splitTime.getTime()) &&
        splitTime > downtime.timeOn &&
        (downtime.timeOff == null || splitTime < downtime.timeOff)
      )
    }

    // the date dialog can only show whole seconds, 
    // so we can only split on whole seconds,
    // so we should not offer the option to split on alarms that are
    // within the same second as the start of the downtime event
    const roundDownToStartOfSecond = (date: Date) : Date => {
      let dateCopy = new Date(date)
      dateCopy.setMilliseconds(0)
      return dateCopy;
    }

    const renderClickableAction = ({ original }: { original: Alarm }) =>
      roundDownToStartOfSecond(original.timeOn) > downtime.timeOn &&
      (!downtime.timeOff || original.timeOn < downtime.timeOff) && (
        <div style={{ textAlign: 'center' }}>
          <Button
            onClick={() =>
              this.setState({
                splitTime: original.timeOn,
                validated: true
              })
            }
          >
            Split
          </Button>
        </div>
      )

    return (
      <Modal
        show={downtime !== null}
        onHide={() => this.props.close()}
        centered
        dialogClassName="modal-90w"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Details of Downtime Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container className="no-max-width">
            <Row>
              <Col className="field-name" xs={2}>
                Scada Object
              </Col>
              <Col xs={4}>{downtime.scadaObjectName}</Col>
              <Col className="field-name" xs={2}>
                Alarm Code
              </Col>
              <Col xs={4}>{downtime.alarmCode}</Col>
            </Row>
            <Row>
              <Col className="field-name" xs={2}>
                Description
              </Col>
              <Col xs={4}>{downtime.description}</Col>
              <Col className="field-name" xs={2}>
                Comment
              </Col>
              <Col xs={4}>{downtime.comment}</Col>
            </Row>
            <Row>
              <Col className="field-name" xs={2}>
                Start
              </Col>
              <Col xs={4}>{this.props.dateFormatter(downtime.timeOn, fromDate)}</Col>
              <Col className="field-name" xs={2}>
                End
              </Col>
              <Col xs={4}>{this.props.dateFormatter(downtime.timeOff, fromDate)}</Col>
            </Row>
            <Row>
              <Col className="field-name" xs={2}>
                Total Duration
              </Col>
              <Col xs={4}>{DurationFormatter(durationInMiliSeconds(downtime))}</Col>
              <Col className="field-name" xs={2}>
                Priority
              </Col>
              <Col xs={4}>{downtime.priority}</Col>
            </Row>
            <Row>
              <Col className="field-name" xs={2}>
                Group
              </Col>
              <Col xs={4}>{downtime.group}</Col>
              <Col className="field-name" xs={2}>
                Sub Group
              </Col>
              <Col xs={4}>{downtime.subGroup}</Col>
            </Row>
            <Row>
              <Col className="field-name" xs={2}>
                Lost Generation
              </Col>
              <Col xs={4}>
                {downtime.lostProduction === undefined
                  ? 'Not calculated'
                  : downtime.lostProduction.toFixed(2) + ' MWh'}
              </Col>
            </Row>
            <Row>
              <Col className="field-name" xs={2}>
                Mean{' '}
                {getTechnologyString(siteTechnology,
                  'Wind Speed',
                  'Irradiance',
                  '')}
              </Col>
              <Col xs={4}>
                {downtime.meanWindSpeed === undefined
                  ? 'Not calculated'
                  : downtime.meanWindSpeed.toFixed(2) +
                    getTechnologyString(siteTechnology,
                      ' m/s',
                      ' W/m²',
                      '')}
              </Col>
              <Col className="field-name" xs={2}>
                {getTechnologyString(siteTechnology,
                  'Wind Speed',
                  'Irradiance',
                  '')}
                Data Coverage
              </Col>
              <Col xs={4}>
                {downtime.windSpeedDataCoverage === undefined
                  ? 'Not calculated'
                  : (downtime.windSpeedDataCoverage * 100).toFixed(0) + '%'}
              </Col>
            </Row>
            <Row>
              <Col className="field-name" xs={2}>
                Last Modified By
              </Col>
              <Col xs={4}>{downtime.lastModifiedBy}</Col>
              <Col className="field-name" xs={2}>
                Last Modified On
              </Col>
              <Col xs={4}>{this.props.dateFormatter(downtime.dateLastModified, fromDate)}</Col>
            </Row>
          </Container>
          <div className="parent grow">
            <h6 className="margin-top-20">Alarms Making up the Downtime Event</h6>
            <ReactTable
              sortable={false}
              showPagination={false}
              showPaginationBottom={false}
              pageSize={alarms ? alarms.length : 0}
              data={alarms || []}
              loading={loadingAlarms}
              loadingText={'Loading'}
              noDataText={'No alarms found for the selected downtime'}
              ref={element => {
                if (element) {
                  // from https://github.com/tannerlinsley/react-table/issues/495#issuecomment-331946312
                  this.dataTableElement = element
                  let theads = (ReactDOM.findDOMNode(element) as Element).getElementsByClassName(
                    'rt-thead'
                  )
                  let tbody = (ReactDOM.findDOMNode(element) as Element).getElementsByClassName(
                    'rt-tbody'
                  )[0]

                  tbody.addEventListener('scroll', () => {
                    for (let i = 0; i < theads.length; i++) {
                      theads[i].scrollLeft = tbody.scrollLeft
                    }
                  })
                }
              }}
              className={'fixed-height-data-table grow parent'}
              getTheadTrProps={state => ({ style: { minWidth: state.rowMinWidth } })}
              getTrProps={(state: any) => ({
                style: { minWidth: state.rowMinWidth },
                className: 'wordwrap'
              })}
              columns={[
                {
                  accessor: 'description',
                  Header: 'Description',
                  Cell: row => <span className="wordwrap">{row.value}</span>
                },
                {
                  accessor: 'alarmDefinitionCode',
                  Header: 'Code',
                  Cell: row => <span className="wordwrap">{row.value}</span>
                },
                {
                  accessor: 'timeOn',
                  Header: 'Time On',
                  Cell: row => (
                    <span className="wordwrap">
                      {this.props.dateFormatter(row.value, fromDate)}
                    </span>
                  )
                },
                {
                  accessor: 'timeOff',
                  Header: 'Time Off',
                  Cell: row => (
                    <span className="wordwrap">
                      {this.props.dateFormatter(row.value, fromDate)}
                    </span>
                  )
                },
                {
                  accessor: row => durationInMiliSeconds(row),
                  id: 'duration',
                  Header: <span title="Duration is not restricted to selected period (HH:mm:ss)">Duration</span>,
                  Cell: row => <span className="wordwrap">{DurationFormatter(row.value)}</span>
                },
                {
                  Header: 'Action',
                  minWidth: 70,
                  maxWidth: 70,
                  Cell: renderClickableAction
                }
              ]}
              defaultSorted={[
                {
                  id: 'timeOn',
                  desc: false
                }
              ]}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Form>
            <Row>
              <Col className="col-auto">
                <Form.Group as={Row}>
                  <Col className="col-auto">
                    <Form.Label>Split Time</Form.Label>
                  </Col>
                  <Col className="col-auto">
                    <DateTimePicker
                      onChange={(date?: Date | undefined) => {
                        handleSplit(date)
                      }}
                      onBlur={(date?: Date | undefined) => {
                        handleSplit(date)
                      }}
                      value={splitTime}
                      inputClass={validated ? 'form-control is-valid' : 'form-control is-invalid'}
                      displayCalendarAbove={true}
                    />
                    <Form.Control hidden={true} isValid={validated} isInvalid={!validated} />
                    <Form.Control.Feedback type="invalid">
                      {downtime.timeOff ? (
                        <p>
                          Split time must be between{' '}
                          {this.props.dateFormatter(downtime.timeOn, fromDate)} and{' '}
                          {this.props.dateFormatter(downtime.timeOff, fromDate)}
                        </p>
                      ) : (
                        <p>
                          Split time must be after{' '}
                          {this.props.dateFormatter(downtime.timeOn, fromDate)}
                        </p>
                      )}
                    </Form.Control.Feedback>
                  </Col>
                </Form.Group>
              </Col>
              <Col className="col-auto">
                <ButtonGroup>
                  <Button
                    type="submit"
                    disabled={!validated}
                    variant="primary"
                    onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                      e.preventDefault()
                      if (!splitTime) {
                        return // The button should be disabled anyway
                      }
                      this.props.split(downtime, splitTime)
                    }}
                  >
                    Split Downtime
                  </Button>
                  <Button variant="warning" onClick={() => this.props.close()}>
                    Cancel
                  </Button>
                </ButtonGroup>
              </Col>
            </Row>
          </Form>
        </Modal.Footer>
      </Modal>
    )
  }
}
